<qai-toast [ariaLabel]="'learningPathway.NOTIFICATIONS_ARIA' | transloco" />

<div class="pathway-header" *ngIf="isTranslationLoaded">
  <qai-inner-wrap>
    <!-- Shown when assessment personalization fails — flag is set in call-pathway and read on init -->
    <qai-alert
      *ngIf="showAssessmentPersonalizationError"
      [kind]="'error'"
      [title]="
        'learningPathway.ASSESSMENT_PERSONALIZATION_ERROR_TITLE' | transloco
      "
      class="alert-spacing assessment-personalization-alert"
      (closeClick)="closeClick($event)"
    >
      {{
        "learningPathway.ASSESSMENT_PERSONALIZATION_ERROR_DESC_START"
          | transloco
      }}<a href="mailto:help@quantified.com">help@quantified.com</a
      >{{
        "learningPathway.ASSESSMENT_PERSONALIZATION_ERROR_DESC_END" | transloco
      }}
    </qai-alert>

    <!-- Loading State -->
    <div *ngIf="loading" class="loading-container">
      <qai-loading [size]="'small'"></qai-loading>
      <p class="loading-text">Loading...</p>
    </div>

    <qai-alert
      *ngIf="!loading && isEnrollmentCompleted"
      [kind]="'success'"
      [title]="'learningPathway.PATHWAY_COMPLETED_TITLE' | transloco"
      class="alert-spacing"
    >
      {{ "learningPathway.PATHWAY_COMPLETED_BODY" | transloco }}
      <div class="alert-action">
        <button
          qai-button
          kind="tertiary-text"
          size="small"
          (click)="goToLearnerDashboard()"
        >
          {{ "learningPathway.PATHWAY_COMPLETED_RETURN" | transloco }}
        </button>
      </div>
    </qai-alert>

    <qai-alert
      *ngIf="!loading && hasStepOutOfAttempts"
      [kind]="'error'"
      [title]="'learningPathway.PATHWAY_OUT_OF_ATTEMPTS_TITLE' | transloco"
      class="alert-spacing"
    >
      {{ "learningPathway.PATHWAY_OUT_OF_ATTEMPTS_BODY" | transloco }}
    </qai-alert>

    <!-- Step Flow - Only show when not loading and steps are available -->
    <qai-step-flow
      *ngIf="!loading && pathwaySteps.length > 0"
      [animated]="animateStepFlow"
      (allStepsComplete)="onAllStepsComplete()"
    >
      @for (step of pathwaySteps; track step.stepUuid) {
        <qai-step-card
          [title]="step.title"
          [description]="step.description"
          [optional]="step.optional"
          [ctaLabel]="step.ctaLabel"
          [ctaTooltipText]="step.ctaTooltipText"
          [state]="step.state"
          (ctaClick)="step.ctaLabel ? onStepClick(step) : null"
          [ctaButtonVisible]="isStepCtaButtonVisible(step)"
          [showHeader]="isStepHeaderVisible(step)"
        >
          @if (step.optional) {
            <qai-badge
              slot="title-badge"
              shape="round"
              size="xsmall"
              kind="gray"
              [label]="'learningPathway.OPTIONAL' | transloco"
            ></qai-badge>
          }
          @if (step.stepType === PathwayStepType.QUIZ) {
            <qai-step-table
              [title]="step.title"
              [disabled]="step.state === 'disabled'"
              [isComplete]="step.state === 'completed' && quizHasAttempts(step)"
              [optional]="step.optional"
              [borderStateOverride]="step.optional && step.state === 'completed' && !quizHasAttempts(step) ? 'complete' : undefined"
              [showCompleteBadge]="false"
              [attemptsRemaining]="step.attemptsRemaining"
              [instructionText]="step.description"
              [attempts]="getSortedQuizAttempts(step)"
              [overallScoreTooltip]="getOverallScoreTooltip()"
              [columnHeaders]="getQuizColumnHeaders()"
              [showAllAttemptsLink]="quizHasAttempts(step)"
              [actionHeader]="
                step.state === 'completed' ? { variant: 'link' } : undefined
              "
              (beginClick)="onStepClick(step)"
              (reattemptClick)="onStepClick(step)"
              (viewFeedbackClick)="onViewFeedbackClick($event)"
              (viewAllAttemptsClick)="onViewAllAttemptsClick(step)"
            >
              @if (step.optional) {
                <qai-badge
                  slot="title-badge"
                  shape="round"
                  size="xsmall"
                  kind="gray"
                  [label]="'learningPathway.OPTIONAL' | transloco"
                ></qai-badge>
              }
            </qai-step-table>
          }
          @if (isRoleplayStepType(step.stepType)) {
            <qai-step-table
              [title]="step.title"
              [disabled]="step.state === 'disabled'"
              [isComplete]="step.state === 'completed'"
              [optional]="step.optional"
              [showCompleteBadge]="false"
              [instructionText]="step.description"
              [attemptsRemaining]="step.attemptsRemaining"
              [attempts]="callStepService.getRolePlayStepAttempts(step)"
              [pageSize]="callStepService.getRolePlayStepPageSize(step)"
              [columnHeaders]="
                callStepService.getRolePlayStepColumnHeaders(step)
              "
              [showAllAttemptsLink]="
                (step.practiceCallAttempts?.length ?? 0) >= 1
              "
              [showAttemptsBadge]="
                callStepService.getRolePlayStepShowAttemptsBadge(step)
              "
              [enableRowErrorMessage]="true"
              [showHeaderCta]="callStepService.shouldShowHeaderCta(step)"
              [actionHeader]="callStepService.getRolePlayStepActionHeader(step)"
              [footerSection]="
                callStepService.getRolePlayStepFooterSection(step)
              "
              [attemptsToggleLabels]="
                callStepService.getRolePlayStepToggleLabels(step.stepType)
              "
              (beginClick)="onRolePlayStepActionClick(step)"
              (reattemptClick)="onRolePlayStepActionClick(step)"
              (headerCtaClick)="onRolePlayStepActionClick(step)"
              (viewFeedbackClick)="onViewFeedbackClick($event, true)"
              (viewAllAttemptsClick)="onViewAllAttemptsClick(step)"
              (footerSectionCtaClick)="onRolePlayStepFooterCtaClick(step)"
            >
              @if (step.optional) {
                <qai-badge
                  slot="title-badge"
                  shape="round"
                  size="xsmall"
                  kind="gray"
                  [label]="'learningPathway.OPTIONAL' | transloco"
                ></qai-badge>
              }
            </qai-step-table>
          }
        </qai-step-card>
      }
    </qai-step-flow>
  </qai-inner-wrap>
</div>
